import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { IconButton, cn } from '@cyphex/ui';
import type { VoiceClip } from '@cyphex/types';
import { formatDuration } from '../../lib/time';
import { Waveform } from './Waveform';

interface VoiceMessageProps {
  clip: VoiceClip;
  className?: string;
}

const TICK_MS = 100;

/**
 * Voice note with scrubbable waveform. Uses a real `<audio>` element when the
 * clip has a URL; with the mock adapter (no object storage) it advances a
 * local clock so the transport still behaves correctly.
 */
export const VoiceMessage = memo(function VoiceMessage({ clip, className }: VoiceMessageProps) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const [playing, setPlaying] = useState(false);
  const [elapsedMs, setElapsed] = useState(0);

  const stopTimer = useCallback(() => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
  }, []);

  useEffect(() => stopTimer, [stopTimer]);

  const toggle = useCallback(() => {
    if (playing) {
      audio.current?.pause();
      stopTimer();
      setPlaying(false);
      return;
    }
    setPlaying(true);
    if (clip.url) {
      const element = audio.current ?? new Audio(clip.url);
      audio.current = element;
      element.currentTime = elapsedMs / 1000;
      void element.play();
      element.onended = () => {
        setPlaying(false);
        setElapsed(0);
      };
    }
    timer.current = setInterval(() => {
      setElapsed((current) => {
        const next = current + TICK_MS;
        if (next >= clip.durationMs) {
          stopTimer();
          setPlaying(false);
          return 0;
        }
        return next;
      });
    }, TICK_MS);
  }, [clip.durationMs, clip.url, elapsedMs, playing, stopTimer]);

  const seek = useCallback(
    (fraction: number) => {
      const next = Math.round(fraction * clip.durationMs);
      setElapsed(next);
      if (audio.current) audio.current.currentTime = next / 1000;
    },
    [clip.durationMs],
  );

  const remaining = Math.max(0, clip.durationMs - elapsedMs);

  return (
    <div
      className={cn(
        'flex max-w-bubble items-center gap-3 rounded-lg border border-border bg-card px-3 py-2',
        className,
      )}
    >
      <IconButton
        icon={playing ? Pause : Play}
        label={playing ? 'Pause voice message' : 'Play voice message'}
        variant="primary"
        size="md"
        round
        onClick={toggle}
      />
      <Waveform waveform={clip.waveform} progress={elapsedMs / clip.durationMs} onSeek={seek} />
      <span className="w-10 shrink-0 text-right text-sm tabular text-muted-foreground">
        {formatDuration(playing || elapsedMs > 0 ? remaining : clip.durationMs)}
      </span>
    </div>
  );
});
